import { useState, useEffect } from "react"
import { BookOpen, Settings, DollarSign, Tag, X, Save, Loader2, Clock, Users } from "lucide-react"
import { Button } from "../ui/button"
import { useCategories } from "../../hooks/useCategories"

export default function CourseConfigurationForm({
  course,
  onSave,
  onCancel
}) {
  const [formData, setFormData] = useState({
    categoryId: '',
    price: '',
    originalPrice: '',
    currency: 'XOF',
    isFree: false,
    level: 'BEGINNER',
    maxStudents: 100,
    accessDurationDays: 90,
    isUnlimitedAccess: false,
    duration: 0,
    status: 'DRAFT',
    isFeatured: false,
    certificate: false,
    certificateTemplate: ''
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState({})

  const { categories } = useCategories()

  useEffect(() => {
    if (course) {
      setFormData({
        categoryId: course.categoryId || '',
        price: course.price || '',
        originalPrice: course.originalPrice || '',
        currency: course.currency || 'XOF',
        isFree: course.isFree || false,
        level: course.level || 'BEGINNER',
        maxStudents: course.maxStudents || 100,
        accessDurationDays: course.accessDurationDays || 90,
        isUnlimitedAccess: course.accessDurationDays === null,
        duration: course.duration ? Math.round(course.duration / 60) : 0, // Convert seconds to minutes
        status: course.status || 'DRAFT',
        isFeatured: course.isFeatured || false,
        certificate: course.certificate || false,
        certificateTemplate: course.certificateTemplate || ''
      })
    }
  }, [course])

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    try {
      // Convert duration from minutes to seconds
      const { isUnlimitedAccess, ...dataToSave } = formData
      const finalData = {
        ...dataToSave,
        duration: formData.duration ? formData.duration * 60 : 0,
        accessDurationDays: isUnlimitedAccess ? null : formData.accessDurationDays
      }
      await onSave(finalData)
    } catch (error) {
      console.error('Error saving course configuration:', error)
      setErrors({ general: 'Erreur lors de la sauvegarde' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-[#141835] rounded-lg">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Configuration du cours</h2>
              <p className="text-sm text-gray-600 mt-1">{course?.title || 'Cours'}</p>
            </div>
          </div>
          {onCancel && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="h-8"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <form onSubmit={handleSubmit} className="max-w-4xl mx-auto">
          <div className="space-y-6">
            {/* Catégorie */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Catégorie
              </h3>
              <div className="space-y-4">
                {categories && categories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Catégorie
                    </label>
                    <select
                      name="categoryId"
                      value={formData.categoryId}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    >
                      <option value="">Sélectionner une catégorie</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.displayName || cat.translatedName || cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </div>

            {/* Tarification */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Tarification
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isFree"
                    checked={formData.isFree}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">Cours gratuit</label>
                </div>
                {!formData.isFree && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prix ({formData.currency})
                      </label>
                      <input
                        type="number"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prix original ({formData.currency})
                      </label>
                      <input
                        type="number"
                        name="originalPrice"
                        value={formData.originalPrice}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        min="0"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Devise
                      </label>
                      <select
                        name="currency"
                        value={formData.currency}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                      >
                        <option value="XOF">XOF</option>
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Paramètres */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Paramètres
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Niveau
                  </label>
                  <select
                    name="level"
                    value={formData.level}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  >
                    <option value="BEGINNER">Débutant</option>
                    <option value="INTERMEDIATE">Intermédiaire</option>
                    <option value="ADVANCED">Avancé</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <Users className="w-4 h-4" />
                      Étudiants maximum
                    </label>
                    <input
                      type="number"
                      name="maxStudents"
                      value={formData.maxStudents}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Durée d'accès (jours)
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        name="accessDurationDays"
                        value={formData.accessDurationDays}
                        onChange={handleChange}
                        className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                        min="1"
                        disabled={formData.isUnlimitedAccess}
                      />
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name="isUnlimitedAccess"
                          checked={formData.isUnlimitedAccess}
                          onChange={handleChange}
                          className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                        />
                        <label className="text-sm text-gray-700">Illimité</label>
                      </div>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {formData.isUnlimitedAccess 
                        ? "L'accès au cours est illimité (accessExpiresAt sera null)" 
                        : "L'accès expirera après le nombre de jours spécifié"}
                    </p>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Durée du cours (minutes)
                  </label>
                  <input
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                    min="0"
                    placeholder="Durée totale du contenu du cours en minutes"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Durée totale du contenu vidéo et des chapitres du cours
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={formData.isFeatured}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">Cours en vedette</label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Statut du cours
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                  >
                    <option value="DRAFT">Brouillon</option>
                    <option value="PUBLISHED">Publié</option>
                    <option value="ARCHIVED">Archivé</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {formData.status === 'DRAFT' && 'Le cours est en brouillon et n\'est pas visible par les étudiants'}
                    {formData.status === 'PUBLISHED' && 'Le cours est publié et visible par les étudiants'}
                    {formData.status === 'ARCHIVED' && 'Le cours est archivé et n\'est plus accessible'}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="certificate"
                    checked={formData.certificate}
                    onChange={handleChange}
                    className="w-4 h-4 text-[#141835] border-gray-300 rounded focus:ring-[#141835]"
                  />
                  <label className="text-sm text-gray-700">Certificat disponible</label>
                </div>
                {formData.certificate && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modèle de certificat
                    </label>
                    <input
                      type="text"
                      name="certificateTemplate"
                      value={formData.certificateTemplate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#141835]"
                      placeholder="certificate-template-v1"
                    />
                  </div>
                )}
              </div>
            </div>

            {errors.general && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-600">{errors.general}</p>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </Button>
        )}
        <Button
          type="submit"
          onClick={handleSubmit}
          disabled={saving}
          className="bg-[#141835] hover:bg-[#1a1f4a] text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Sauvegarde...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Sauvegarder la configuration
            </>
          )}
        </Button>
      </div>
    </div>
  )
}

